<template>
  <div>
    <my-notification :icon="'book'">
      <template #title>
        {{ $t('compTransitionDocsManual.title') }}
      </template>
    </my-notification>
    <br />
    <b-tabs>
      <b-tab-item icon="info" :label="$t('compTransitionDocsManual.title-1')">
        <b-notification :closable="false">
          <strong>{{ $t('compTransitionDocsManual.manual-1-1') }}</strong>
          <br />
          {{ $t('compTransitionDocsManual.manual-1-2') }}
          <ul style="list-style-type: square; margin-left: 25px">
            <li><span v-html="$t('compTransitionDocsManual.manual-1-3')" /></li>
            <li><span v-html="$t('compTransitionDocsManual.manual-1-4')" /></li>
          </ul>
          {{ $t('compTransitionDocsManual.manual-1-5') }}
        </b-notification>

        <b-notification :closable="false">
          <strong>{{ $t('compTransitionDocsManual.manual-1-6') }}</strong
          ><br />
          <span v-html="$t('compTransitionDocsManual.manual-1-6-1')" />
          <br />
          <span v-html="$t('compTransitionDocsManual.manual-1-6-2')" />
        </b-notification>
      </b-tab-item>

      <!-- editors -->
      <b-tab-item icon="pencil" :label="$t('compTransitionDocsManual.title-2')">
        <p>
          Ce guide a pour objectif de donner des recommandations sur la
          rédaction d’actions soumises par les
          {{ $t('compTransitionDocsManual.structures') }} sur la plateforme
          Transition 1point 5 afin que le réseau permette aux
          {{ $t('compTransitionDocsManual.structures') }} d’aller vers des
          pratiques de recherche plus vertueuses et respectueuses de
          l’environnement.
        </p>
        <b-notification :closable="false">
          <strong>Zoom sur la ligne éditoriale des actions</strong>
          <br />
          <p>
            Pour que la plateforme remplisse sa mission d’échange et de partage
            entre les membres du réseau, les actions décrites doivent être
            rédigées de façon détaillée.
          </p>
        </b-notification>

        <b-notification :closable="false">
          <strong>Le titre et les dates</strong>
          <br />
          <p>
            Titre de l’action: veillez à donner un titre qui donne envie de lire
            le contenu de l’action. Utilisez des verbes d’action ou leur dérivé.
            Exemple: «Instaurer un quota carbone pour les déplacements
            professionnels» ou «Instauration d’un quota carbone pour les
            déplacements professionnels».
          </p>
          <p>
            Une action a une date de début et peut avoir une date de fin. Par
            exemple, l’organisation d’une fresque du climat qui se déroule le 23
            octobre commence à cette date et se termine à cette date également.
            Si la date de fin de fin n’est pas renseignée, l’action est « en
            cours » (par exemple, des quotas carbone sur les missions qui sont
            en vigueur). Si l’action a été abandonnée, il faudra le signaler en
            cochant le bouton prévu à cet effet et détailler dans la description
            les raisons de cet abandon.
          </p>
        </b-notification>

        <b-notification :closable="false">
          <strong>Les labels associés à l’action</strong>
          <br />
          <p>
            Les labels permettent d’attribuer des catégories à chaque action.
            Pour caractériser au mieux une action, n’hésitez pas à mettre
            plusieurs labels car ceux-ci permettront aux membres du réseau de
            faire des recherches ciblées en filtrant par les labels.
          </p>
          <action-tag-list :tags="tags" />
          <br />
          <br />
          <strong>Les catégories de labels:</strong>
          <div class="content">
            <ul>
              <li>
                Les labels de couleur verte ont trait aux différents postes
                d’émissions estimés par l’outil GES 1point5. Au nombre de 6, il
                s’agit de Bâtiments, Matériel informatique, Achats, Missions,
                Déplacements domicile-travail et Véhicules.
              </li>
              <li>
                Les labels de couleur bleue permettent de catégoriser l’action
                décrite en peu de mots de sorte que l’utilisateur comprenne
                rapidement de quoi il s’agit. Exemple : le label « alimentation
                » peut être utilisé pour décrire toute action visant à favoriser
                une alimentation plus durable, le label « gouvernance » peut
                être utilisé pour décrire toute action touchant aux modes
                d’organisation dans la structure. Au nombre d'une vingtaine à la
                fin de l’année 2023, il sera possible d’en ajouter d’autres si
                le besoin est exprimé.
              </li>
              <li>
                Les labels de couleur beige permettent d’indiquer si l’action
                est obligatoire ou facultative.
              </li>
              <li>
                Les labels de couleur orange, au nombre de 4 en fin d’année
                2023, indiquent au lecteur la manière dont l’action a été mise
                en place. Par exemple, cela peut provenir d’une « Décision de la
                direction » ou d’un « Échange en AG d’unité » associée à une «
                Décision de la direction ». Dans le champ description, nous
                conseillons vivement aux rédacteurs des actions de revenir plus
                en détail sur la manière dont l’action a été mise en place au
                sein de la structure.
              </li>
              <li>
                Les labels de couleur rose désignent trois périmètres possibles
                pour une action : une échelle individuelle (par exemple un quota
                carbone individuel de déplacement aérien), une échelle équipe
                (par exemple un quota carbone de déplacement aérien à gérer
                collectivement dans une équipe) et une échelle structure (par
                exemple organisation d’un séminaire de présentation des enjeux
                environnementaux au niveau
                {{ $t('compTransitionDocsManual.structure') }}.
              </li>
              <li>
                Les labels de couleur orange foncé désignent le statut de la
                personne qui a porté le travail lié à l'action ou aux actions
              </li>
            </ul>
            <strong>Bon à savoir :</strong> une bulle d’aide apparaît lorsque
            vous passez votre souris sur un label. Aidez-vous de ces indications
            pour attribuer les labels d’une action.
          </div>
        </b-notification>

        <b-notification :closable="false">
          <strong>Description de l'action</strong>
          <br />
          <p>
            Un éditeur vous permet de décrire l’action. Essayez d’être le plus
            complet possible en vous mettant à la place du lecteur et en
            imaginant les questions qu’il pourrait vous poser. Par exemple: pour
            un abri à vélos sécurisé, il faut indiquer le nombre de places qu’il
            contient, le coût approximatif de cette action, sur quel budget cet
            argent a été pris, si une communication a été faite pour informer
            les utilisateurs de vélos, etc. L’objectif est de donner envie aux
            autres {{ $t('compTransitionDocsManual.structure') }} de mettre en
            place une action similaire et de leur donner des informations
            précises pour qu’ils puissent s’inspirer de votre expérience. Ainsi,
            les difficultés ou obstacles rencontrés sont des informations
            intéressantes pour les utilisateurs observateurs du réseau. Le champ
            Description est limité à xx lignes/caractères. Vous pouvez insérer
            des liens hypertexte qui renvoient à des pages web de votre site
            institutionnel.
          </p>

          <br />
          <strong>Exemple</strong>
          <img src="/static/transition/example.png" />
          <p>
            l’action décrite concerne une charte d’éco-responsabilité (label
            charte). Cette charte a été établie au bout d’un processus assez
            long impliquant discussion en conseil de laboratoire, Assemblée
            générale, vote et décision de la direction. Cette charte concerne
            toute la structure et il est obligatoire de la suivre. Elle ne
            concerne que trois postes d’émissions en vert : matériel
            informatique, missions et déplacement domicile-travail et fait
            référence à plusieurs types d’actions en bleu.
          </p>
        </b-notification>

        <b-notification :closable="false">
          <strong>Joindre un fichier</strong>
          <br />

          <p>
            Si vous pensez que cela peut être utile, vous pouvez joindre un
            fichier (par exemple, une charte, un plan d’actions, etc.). La
            taille est limitée à 5 Mo. Ces documents seront partagés au sein du
            réseau. Si nécessaire, pensez à préciser les autorisations de
            diffusion souhaitées: CC-BY-NC-SA, un contact pour autorisation de
            diffusion, ...
          </p>
        </b-notification>

        <b-notification :closable="false">
          <strong>Modération/validation des actions décrites </strong>
          <br />

          <p>
            Tant que vous n’avez pas cliqué sur le bouton Soumettre, votre
            action est dans l’état Brouillon et vous pouvez la modifier. Une
            fois soumise, vous n’avez plus la main sur la saisie du texte ou des
            labels de cette action. Elle est maintenant accessible à l’équipe de
            Modération qui la relit et vérifie sa conformité avec la ligne
            éditoriale des actions du réseau telle que décrite dans ce document.
            L’équipe de Modération pourra prendre contact avec vous afin de vous
            proposer des reformulations pertinentes ou des ajouts dans une
            optique de progression collective. L'équipe de modération se réserve
            le droit de modifier les labels attribués à une action pour garantir
            l'homogénéité des actions publiées et permettre les analyses des
            actions de réduction de l'empreinte environnementale des
            laboratoires.<br />
            L’objectif de l’équipe est de valider les actions soumises dans un
            délai de 5 jours ouvrés.
          </p>
          <p>
            <strong>
              L’action validée sera publiée et visible par les membres et les
              observateurs du réseau.
            </strong>
          </p>
        </b-notification>
      </b-tab-item>

      <!-- explorer -->
      <b-tab-item icon="search" :label="$t('compTransitionDocsManual.title-4')">
        <b-notification :closable="false">
          <strong>{{ $t('compTransitionDocsManual.manual-4-1') }}</strong
          ><br />
          <span v-html="$t('compTransitionDocsManual.manual-4-1-1')" />
          <ul style="list-style-type: square; margin-left: 25px">
            <li>
              <span v-html="$t('compTransitionDocsManual.manual-4-1-2')" />
            </li>
          </ul>
        </b-notification>

        <b-notification :closable="false">
          <strong>{{ $t('compTransitionDocsManual.manual-4-2') }}</strong
          ><br />
          <span v-html="$t('compTransitionDocsManual.manual-4-2-1')" />
        </b-notification>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script>
import { TAGS } from '@/models/transition/transition.js'

export default {
  name: 'Manual',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'action-tag-list': () => import('@/components/transition/ActionTagList.vue')
  },
  data: function () {
    return {
      tags: TAGS
    }
  },
  computed: {},
  methods: {}
}
</script>
